import React, { useState } from "react";
import { Button } from "antd";
import { Plus } from "lucide-react";
import { DataTable } from "../../components/DataTable";
import { IFaq } from "../../../hooks/api/faq/callbacks";
import { useFaqList, useFaqDelete } from "../../../hooks/api/faq/hooks";
import { getFaqColumns } from "./FaqColumns";
import FaqModal from "./FaqModal";

import { useMessageApi } from "../../../provider/AntdNotification";

const FaqList: React.FC = () => {
  const messageApi = useMessageApi();
  const [qData, setQdata] = useState<{
    page: number;
    limit: number;
    search?: string;
    status?: string;
  }>({
    page: 1,
    limit: 10,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<IFaq | null>(null);

  const { data, isLoading, isError } = useFaqList(qData);
  const deleteMutation = useFaqDelete({
    qData,
    onSuccess: () => messageApi.success("FAQ deleted successfully"),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to delete FAQ"),
  });

  const handleAdd = () => {
    setSelectedFaq(null);
    setModalOpen(true);
  };

  const handleEdit = (record: IFaq) => {
    setSelectedFaq(record);
    setModalOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const columns = getFaqColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
  });

  return (
    <div className="w-full">
      <DataTable
        title={<h1 className="text-xl font-semibold text-slate-800">Frequently Asked Questions (FAQ)</h1>}
        headerActions={
          <Button
            type="primary"
            size="small"
            icon={<Plus size={16} />}
            onClick={handleAdd}
            className="!bg-primary flex items-center gap-2"
          >
            Add FAQ
          </Button>
        }
        columns={columns}
        dataSource={data?.data || []}
        rowKey="_id"
        loading={isLoading}
        isError={isError}
        searchProps={{
          searchValue: qData.search,
          onSearchChange: (v) =>
            setQdata((prev) => ({ ...prev, search: v, page: 1 })),
          placeholder: "Search FAQs...",
          statusValue: qData.status,
          onStatusChange: (v) =>
            setQdata((prev) => ({ ...prev, status: v, page: 1 })),
        }}
        paginationProps={{
          page: qData.page,
          limit: qData.limit,
          total: data?.extra?.total || 0,
          onChange: (page, limit) =>
            setQdata((prev) =>
              limit !== prev.limit ? { ...prev, page: 1, limit } : { ...prev, page },
            ),
        }}
      />
      <FaqModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedItem={selectedFaq}
        qData={qData}
      />
    </div>
  );
};

export default FaqList;
